import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MultiSelectDropdown } from '../unified/MultiSelectDropdown';
import { siftApiClient } from '../../api/siftApiClient';
import { apiVehicleToSIFTVehicle } from '../../api/dataAdapter';
import type {
  BrokerVehiclePoint,
  BrokerBuyerMatch,
  BrokerBoardBuyer,
  BrokerBoardCluster,
  BrokerSliceVinRow,
  BrokerBasketSuggestion,
  ClientViewVinRow,
} from '../../api/types';
import type { SIFTVehicle } from '../../types/sift';
import { BrokerViewDealerCard, type BrokerSellerMeta } from './BrokerViewDealerCard';
import { BrokerViewMap } from './BrokerViewMap';
import { VinDetailsPopout } from './VinDetailsPopout';
import { BuyerBasket, type BasketBuyer, type BasketBuyerInfo, type BasketVin } from './BuyerBasket';
import { BrokerSliceView } from './BrokerSliceView';
import { BrokerBuyerCardHeader, BrokerBuyerCopyId } from './BrokerBuyerCardHeader';
import { BrokerClusterCardHeader } from './BrokerClusterCardHeader';
import type { MatchType } from './BrokerVinTable';
import { PriorityChipRow, FiltersChipRow } from './ChipFilters';
import { BrokerBoardSearch } from './BrokerBoardSearch';
import { SavedFilterSets, type BrokerFilterSet, type BrokerFilterSelection, type BrokerViewAvailable } from './SavedFilterSets';
import {
  readActiveViewRef, writeActiveViewRef, selectionFingerprint,
  type ActiveSavedViewRef, type SavedViewScope,
} from './savedViewState';
import { fetchClusterBasketVins } from './clusterBulkAdd';
import {
  type PriorityKey, type PriceBucketKey,
  type BrokerPriorityCounts, type BrokerFilterState,
  priorityKeyFromIndex,
  priceKeyFromIndex,
  emptyBrokerPriorityCounts,
  vehicleMatchesFilters,
  brokerFilterCount,
  compareDealersByPriorityCounts,
  PRIORITY_INDEX, PRICE_INDEX,
  setBrokerThresholds,
  onBrokerThresholdsChange,
} from './brokerViewTokens';
import { formatIngestAge, formatIngestTs } from '../shared/dataFreshness';
import './BrokerViewDashboard.css';
import { track } from '../../telemetry/usage';

/**
 * A dealer aggregated from the filtered vehicle markers: identity plus the
 * per-severity-tier vehicle counts and total. This is all the slim broker seller
 * card needs — no separate KPI card-body fetch.
 */
interface DealerAggregate {
  seller_id: string;
  seller_name: string;
  total_vehicles: number;
  priority_counts: BrokerPriorityCounts;
}

/** What each Group-by lens is for — surfaced as tab tooltips + an info affordance. */
const GROUP_BY_HELP: Record<'seller' | 'buyer' | 'cluster', string> = {
  seller:
    'SELLER — work stalled inventory by seller. Each card shows a seller\'s in-scope VINs and their urgency mix; expand to the VIN list, then open a VIN to see its ~5 best-matched buyers.',
  buyer:
    'BUYER — work by contact. Ranks buyers by how much of your current inventory they can move (matching reach); add a buyer\'s matching VINs to the outreach basket in one click.',
  cluster:
    'CLUSTER — work by cohort. Groups similar VINs (same make/model + year/mileage/CR band) so you can market a spread of like units to a spread of buyers instead of one at a time.',
};

interface BrokerViewDashboardProps {
  userRole?: string;
  impersonatedRole?: string | null;
}interface FilterMappingRow {
  manager: string;
  territory_market_center: string;
  rds_territory: string;
  seller_name: string;
  territory_id?: string;
  parent_territory_id?: string;
  grandparent_territory_id?: string;
}

/** Buyer-match tightness options offered in the entry filter row. Maps display
 * label ↔ the table's `MatchType` token. Make/Model (tier 1) and Segment (tier 0,
 * the cross-model lens) are included so a broker can filter on any fit level. */
const MATCH_TYPE_OPTIONS: { label: string; value: MatchType }[] = [
  { label: 'Exact', value: 'exact' },
  { label: 'Cohort', value: 'cohort' },
  { label: 'Similar', value: 'similar' },
  { label: 'Make/Model', value: 'makemodel' },
  { label: 'Segment', value: 'related' },
];

export const BrokerViewDashboard: React.FC<BrokerViewDashboardProps> = ({
  impersonatedRole,
}) => {
  // Entry filters. Broker View is open to everyone (no role gating / territory
  // pre-filters), so we keep just Market Center + Seller.
  const [selectedMarketCenters, setSelectedMarketCenters] = useState<string[]>([]);
  const [selectedSellers, setSelectedSellers] = useState<string[]>([]);
  const [selectedAuctions, setSelectedAuctions] = useState<string[]>([]);
  const [vinFilter, setVinFilter] = useState('');

  // Buyer-match tightness filter (Exact / Cohort / Similar), applied server-side
  // in the shared VIN table across all three slices — "show VINs that have a
  // buyer available at this match tier" (§11.5 / §11.10).
  const [selectedMatchTypes, setSelectedMatchTypes] = useState<MatchType[]>([]);

  // Filter option pools
  const [allMarketCenters, setAllMarketCenters] = useState<string[]>([]);
  const [allSellers, setAllSellers] = useState<string[]>([]);
  const [allAuctions, setAllAuctions] = useState<string[]>([]);
  const [filterMapping, setFilterMapping] = useState<FilterMappingRow[]>([]);

  // Chip filter state
  const [selectedPriorities, setSelectedPriorities] = useState<PriorityKey[]>([]);
  const [selectedDaysListed, setSelectedDaysListed] = useState<number[]>([]);
  const [selectedListingOcc, setSelectedListingOcc] = useState<number[]>([]);
  const [selectedTotalBids, setSelectedTotalBids] = useState<number[]>([]);
  const [selectedReprice, setSelectedReprice] = useState<number[]>([]);
  const [selectedVdp, setSelectedVdp] = useState<number[]>([]);
  const [selectedWorkbook, setSelectedWorkbook] = useState<number[]>([]);
  const [selectedPrice, setSelectedPrice] = useState<PriceBucketKey[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [conditionMode, setConditionMode] = useState<'exclude' | 'include'>('exclude');
  const [selectedEngaged, setSelectedEngaged] = useState<string[]>([]);
  const [selectedSupply, setSelectedSupply] = useState<string[]>([]);
  const [selectedDemand, setSelectedDemand] = useState<string[]>([]);
  // Inspection provenance chip (MMI / third-party / any / none).
  const [selectedInspection, setSelectedInspection] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  // §14.17: cross-nav focus — when set, the Buyer tab scopes to this one buyer
  // (set by clicking a VIN's recommended buyer, or the Buyer-tab search §14.18).
  const [focusedBuyers, setFocusedBuyers] = useState<{ uid: string; name: string | null }[]>([]);
  // §14.15: Buyer-tab lost-bidder re-engagement view — sort lost-heavy buyers
  // first and/or keep only buyers with a recent lost bid.
  const [buyerLostFirst, setBuyerLostFirst] = useState(false);
  const [buyerLostOnly, setBuyerLostOnly] = useState(false);

  // Selection
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);
  const [expandedDealerId, setExpandedDealerId] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<SIFTVehicle | null>(null);
  const [vinPopoutOpen, setVinPopoutOpen] = useState(false);
  const [loadingVehicle, setLoadingVehicle] = useState(false);

  // Buyer outreach basket — staged buyers grouped by buyer_uid, each holding the
  // VINs a broker wants to pitch to that contact (batch-per-contact workflow).
  // Persisted per-user in USERS.USER_CONFIG (§10-C): hydrated on mount, then
  // written back debounced so a burst of add/removes collapses into one write.
  const [basket, setBasket] = useState<Map<string, BasketBuyer>>(new Map());
  const [basketOpen, setBasketOpen] = useState(false);
  // Confirms an add without stealing focus — adds used to force the basket open,
  // which broke multi-add runs from the seller lens.
  const [basketFlash, setBasketFlash] = useState(false);
  const basketFlashTimerRef = useRef<number | null>(null);
  const basketHydratedRef = useRef(false);
  const lastPersistedBasketRef = useRef<string>('[]');
  // §5: which cluster is mid bulk-add (spinner on its card button).
  const [bulkAddingCluster, setBulkAddingCluster] = useState<string | null>(null);

  // Map is a secondary view — default OFF so the board opens as a full-width
  // table pane. Toggling it on reveals the split map (still shown automatically
  // whenever a VIN pop-out is open, regardless of this toggle).
  const [showMap, setShowMap] = useState(false);
  // §10-E card→map focus: when set, the right-pane map drops the board layer and
  // shows this seller/buyer/cluster's targeted units + candidate buyers.
  const [mapFocus, setMapFocus] = useState<{ kind: 'seller' | 'buyer' | 'cluster'; key: string; name: string } | null>(null);

  // Carousel pivot: work the board at the seller level (default) or the buyer
  // level (which buyers can move the most of the current work). Cluster (group
  // similar VINs) is a later phase.
  const [groupBy, setGroupBy] = useState<'seller' | 'buyer' | 'cluster'>('seller');
  // Bumped when an admin saves broker settings, so every board query refetches
  // against the new server-side thresholds.
  const [settingsEpoch, setSettingsEpoch] = useState(0);
  // Cluster the Cluster tab should auto-expand when arriving from a VIN pop-out.
  const [pendingClusterKey, setPendingClusterKey] = useState<string | null>(null);

  // §10-E: a focus belongs to one tab's cards, so drop it when the tab changes
  // (the newly-mounted slice also clears it, this covers the seller tab).
  useEffect(() => { setMapFocus(null); }, [groupBy]);

  // Data — aggregated vehicle marker points drive both the map and the carousel
  // list (the carousel groups them per dealer). The KPI-heavy card bodies are
  // fetched per visible page and merged in on render.
  const [vehiclePoints, setVehiclePoints] = useState<BrokerVehiclePoint[]>([]);
  // Lazily-fetched Salesforce account ids per seller (visible page only), used
  // solely to render the card's Salesforce link. Cached across pages.
  const [sellerMeta, setSellerMeta] = useState<Map<string, BrokerSellerMeta>>(new Map());
  const [vinDataUpdated, setVinDataUpdated] = useState<string | null>(null);
  const [buyerDataUpdated, setBuyerDataUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  // The saved view the board is showing. Remembered across a refresh: until the
  // saved-view list answers, the first board fetch is held so it doesn't load the
  // whole unfiltered board only to throw it away.
  const [activeView, setActiveView] = useState<(ActiveSavedViewRef & { selection: BrokerFilterSelection }) | null>(null);
  const [restoreViewRef] = useState<ActiveSavedViewRef | null>(() => readActiveViewRef());
  const [restorePending, setRestorePending] = useState<boolean>(() => restoreViewRef != null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Guards against out-of-order dealer responses: a slow earlier request (e.g. the
  // initial unfiltered load on a cold cache) must not overwrite a fresher filtered
  // result. We bump a sequence id per request and abort the prior in-flight call.
  const fetchSeqRef = useRef(0);
  const fetchAbortRef = useRef<AbortController | null>(null);
  // Tracks whether the initial dealer fetch has fired, so the first load can skip
  // the 300ms filter-debounce and start streaming data immediately.
  const hasFetchedRef = useRef(false);

  // ----- Cascading filter options (Market Center ↔ Seller) -----
  const filteredOptions = useMemo(() => {
    const marketCenters = [...new Set(
      (selectedSellers.length > 0
        ? filterMapping.filter(m => selectedSellers.includes(m.seller_name))
        : filterMapping
      ).map(m => m.territory_market_center),
    )].filter(Boolean).sort();
    const sellers = [...new Set(
      (selectedMarketCenters.length > 0
        ? filterMapping.filter(m => selectedMarketCenters.includes(m.territory_market_center))
        : filterMapping
      ).map(m => m.seller_name),
    )].filter(Boolean).sort();
    return {
      marketCenters: marketCenters.length ? marketCenters : allMarketCenters,
      sellers: sellers.length ? sellers : allSellers,
    };
  }, [filterMapping, selectedMarketCenters, selectedSellers, allMarketCenters, allSellers]);

  // ----- Load filter options -----
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const filterOptions = await siftApiClient.getFilterOptions(impersonatedRole || undefined, true);
        setAllMarketCenters(filterOptions.market_centers || []);
        setAllSellers(filterOptions.sellers || []);
        setAllAuctions(filterOptions.auctions || []);
        setFilterMapping((filterOptions.mapping || []) as FilterMappingRow[]);
        setDefaultsApplied(true);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        setDefaultsApplied(true);
      }
    };
    loadFilters();
  }, [impersonatedRole]);

  // ----- Load the shared Broker thresholds so client-side urgency/price badges
  // match the server (admin-tunable). An admin save pushes the new config into
  // the same module state and notifies here, so the board re-scores and refetches
  // without a page reload. -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await siftApiClient.getBrokerSettings();
        if (!cancelled) setBrokerThresholds(resp.settings);
      } catch (err) {
        console.error('Failed to load broker settings:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Server-side severity/buckets are built from the same settings, so a save has
  // to refetch the board too — not just re-render the client-side badges.
  useEffect(() => onBrokerThresholdsChange(() => setSettingsEpoch((n) => n + 1)), []);

  // ----- Fetch dealers -----
  const fetchDealers = useCallback(async () => {
    // Supersede any in-flight request so its (potentially slower) response can't
    // land after this one and revert the board to a stale/unfiltered set.
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const seq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await siftApiClient.getBrokerViewVehiclePoints({
        market_center: selectedMarketCenters.length > 0 ? selectedMarketCenters.join(',') : undefined,
        seller_name: selectedSellers.length > 0 ? selectedSellers.join(',') : undefined,
        auction: selectedAuctions.length > 0 ? selectedAuctions.join(',') : undefined,
        vin: vinFilter.trim() ? vinFilter.trim() : undefined,
        match_types: selectedMatchTypes.length > 0 ? selectedMatchTypes.join(',') : undefined,
        // §8.4 / §14.5 non-bucketed filters are applied server-side on the markers
        // (the Seller map + carousel filter the rest client-side by bucket index).
        conditions: selectedConditions.length ? selectedConditions.join(',') : undefined,
        conditions_mode: selectedConditions.length ? conditionMode : undefined,
        supply: selectedSupply.length ? selectedSupply.join(',') : undefined,
        demand: selectedDemand.length ? selectedDemand.join(',') : undefined,
        inspection: selectedInspection.length ? selectedInspection.join(',') : undefined,
        broker_status: selectedStatus.length ? selectedStatus.join(',') : undefined,
        impersonate_role: impersonatedRole || undefined,
      }, controller.signal);
      // Ignore a response that was superseded while in flight.
      if (seq !== fetchSeqRef.current) return;
      setVehiclePoints(result.points);
      setVinDataUpdated(result.vin_data_last_updated ?? null);
      setBuyerDataUpdated(result.buyer_data_last_updated ?? null);
      setSelectedDealerId(null);
      // Drop any expanded-dealer drawer too: the prior dealer may not be in the new board, and a
      // lingering id would be a stale focus for both the carousel and the agent.
      setExpandedDealerId(null);
    } catch (err: any) {
      // A cancelled request was intentionally superseded — leave state untouched.
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      if (seq !== fetchSeqRef.current) return;
      setError(err?.response?.data?.detail || 'Failed to load vehicle data');
      setVehiclePoints([]);
    } finally {
      // Only the latest request owns the loading flag.
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [selectedMarketCenters, selectedSellers, selectedAuctions, vinFilter, impersonatedRole, selectedMatchTypes, selectedConditions, conditionMode, selectedSupply, selectedDemand, selectedInspection, selectedStatus, settingsEpoch]);

  // Never let a slow or failed saved-view lookup hold the board hostage.
  useEffect(() => {
    if (!restorePending) return;
    const t = setTimeout(() => setRestorePending(false), 5000);
    return () => clearTimeout(t);
  }, [restorePending]);

  useEffect(() => {
    if (!defaultsApplied || restorePending) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // First load (no dealers yet) fires immediately so data starts streaming the
    // moment filter defaults resolve; subsequent filter tweaks stay debounced.
    const delay = hasFetchedRef.current ? 300 : 0;
    hasFetchedRef.current = true;
    debounceRef.current = setTimeout(fetchDealers, delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchDealers, defaultsApplied, restorePending]);

  // ----- Chip filter dataset (vehicle markers) -----
  const filterState: BrokerFilterState = useMemo(() => ({
    priorities: selectedPriorities,
    daysListed: selectedDaysListed,
    listingOcc: selectedListingOcc,
    totalBids: selectedTotalBids,
    reprice: selectedReprice,
    price: selectedPrice,
    vdpViews: selectedVdp,
    workbookSaves: selectedWorkbook,
    engaged: selectedEngaged,
  }), [selectedPriorities, selectedDaysListed, selectedListingOcc, selectedTotalBids, selectedReprice, selectedPrice, selectedVdp, selectedWorkbook, selectedEngaged]);

  const filteredVehiclePoints = useMemo(() => {
    if (brokerFilterCount(filterState) === 0) return vehiclePoints;
    return vehiclePoints.filter((p) => vehicleMatchesFilters(p, filterState));
  }, [vehiclePoints, filterState]);

  // ----- Group the filtered vehicle markers into per-dealer aggregates -----
  // The carousel lists every dealer that owns at least one matching vehicle,
  // each carrying its per-priority-bucket vehicle counts and total.
  const dealerAggregates = useMemo(() => {
    const bySeller = new Map<string, DealerAggregate>();
    for (const p of filteredVehiclePoints) {
      let agg = bySeller.get(p.seller_id);
      if (!agg) {
        agg = {
          seller_id: p.seller_id,
          seller_name: p.seller_name,
          total_vehicles: 0,
          priority_counts: emptyBrokerPriorityCounts(),
        };
        bySeller.set(p.seller_id, agg);
      }
      agg.total_vehicles += p.count;
      agg.priority_counts[priorityKeyFromIndex(p.priority_bucket)] += p.count;
    }
    return Array.from(bySeller.values());
  }, [filteredVehiclePoints]);

  // Sort: lexicographic by priority bucket counts.
  // - With selected priorities: order respects the user's chip selection sequence.
  // - Without selection: PRIORITY_ORDER (most urgent first), ties → seller_name.
  const sortedDealers = useMemo(() => {
    const arr = [...dealerAggregates];
    arr.sort((a, b) => compareDealersByPriorityCounts(
      a.priority_counts, b.priority_counts, a.seller_name, b.seller_name, selectedPriorities,
    ));
    return arr;
  }, [dealerAggregates, selectedPriorities]);

  // Total distinct dealers across the unfiltered board (for the "X of Y" stat).
  const totalDealerCount = useMemo(
    () => new Set(vehiclePoints.map((p) => p.seller_id)).size,
    [vehiclePoints],
  );

  // ----- Pagination -----
  const PAGE_SIZES = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);
  const totalPages = Math.max(1, Math.ceil(sortedDealers.length / pageSize));
  // Reset to page 1 when result set or page size changes
  useEffect(() => { setPage(1); }, [sortedDealers.length, pageSize]);
  const pagedAggregates = useMemo(
    () => sortedDealers.slice((page - 1) * pageSize, page * pageSize),
    [sortedDealers, page, pageSize]
  );

  // Lazily fetch the visible page's seller meta (Seller lens only): the Salesforce
  // link plus the RDS contact recency already on the card payload, so a broker can
  // see that a rep just touched this seller before reaching out themselves.
  const sfAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (groupBy !== 'seller') return;
    const missing = pagedAggregates
      .filter((d) => !sellerMeta.has(d.seller_id))
      .map((d) => d.seller_id);
    if (missing.length === 0) return;
    sfAbortRef.current?.abort();
    const controller = new AbortController();
    sfAbortRef.current = controller;
    let cancelled = false;
    (async () => {
      try {
        const res = await siftApiClient.getBrokerViewDealerCards(missing, controller.signal);
        if (cancelled) return;
        setSellerMeta((prev) => {
          const next = new Map(prev);
          for (const c of res.cards) {
            next.set(c.seller_id, {
              salesforceId: c.salesforce_id ?? null,
              daysSinceLastContact: c.days_since_last_contact ?? null,
              lastContactDate: c.last_contact_date ?? null,
              contactCount30d: c.sfdc_contact_count_30d ?? 0,
              lastContactAgent: c.last_contact_agent ?? null,
            });
          }
          // Mark any that came back without a card as resolved-but-absent.
          for (const id of missing) {
            if (!next.has(id)) {
              next.set(id, { salesforceId: null, daysSinceLastContact: null, lastContactDate: null, contactCount30d: 0, lastContactAgent: null });
            }
          }
          return next;
        });
      } catch {
        if (controller.signal.aborted) return;
      }
    })();
    return () => { cancelled = true; };
  }, [pagedAggregates, sellerMeta, groupBy]);

  // When the selected dealer changes (e.g. user clicks a map marker), make
  // sure the carousel jumps to the page containing that dealer and scrolls
  // its card into view.
  const carouselListRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selectedDealerId) return;
    const idx = sortedDealers.findIndex((d) => d.seller_id === selectedDealerId);
    if (idx < 0) return;
    const targetPage = Math.floor(idx / pageSize) + 1;
    if (targetPage !== page) {
      setPage(targetPage);
      return; // next render will re-run this effect with the new page
    }
    const localIdx = idx - (page - 1) * pageSize;
    const list = carouselListRef.current;
    if (!list) return;
    const child = list.children[localIdx] as HTMLElement | undefined;
    if (child && typeof child.scrollIntoView === 'function') {
      child.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedDealerId, sortedDealers, pageSize, page]);

  // ----- Carousel ↔ map splitter -----
  // Default the carousel to half the viewport so cards open centered, with the
  // map covering the other half. Still freely draggable between the clamps below.
  const [carouselWidth, setCarouselWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? Math.floor(window.innerWidth / 2) : 600,
  );
  const splitterRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ startX: number; startW: number } | null>(null);

  const onSplitterMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragStateRef.current = { startX: e.clientX, startW: carouselWidth };
    const onMove = (ev: MouseEvent) => {
      const s = dragStateRef.current;
      if (!s) return;
      // Keep at least ~320px of map visible on the right while allowing the
      // carousel to grow well past the old 720px cap (half-page default).
      const maxW = Math.max(360, window.innerWidth - 320);
      const next = Math.min(maxW, Math.max(260, s.startW + (ev.clientX - s.startX)));
      setCarouselWidth(next);
    };
    const onUp = () => {
      dragStateRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Nudge map to recompute its size after drag ends
      window.dispatchEvent(new Event('resize'));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [carouselWidth]);

  // ----- Chip counts (sum VEHICLES per bucket over the full board) -----
  const priorityCounts = useMemo(() => {
    const counts = emptyBrokerPriorityCounts();
    for (const p of vehiclePoints) counts[priorityKeyFromIndex(p.priority_bucket)] += p.count;
    return counts;
  }, [vehiclePoints]);

  // Per-bucket-index vehicle counts for the custom filter groups (marker payload
  // carries the matching bucket index per VIN).
  const bucketCounts = (pick: (p: BrokerVehiclePoint) => number) => {
    const counts: number[] = [];
    for (const p of vehiclePoints) {
      const idx = pick(p);
      if (idx >= 0) counts[idx] = (counts[idx] ?? 0) + p.count;
    }
    return counts;
  };
  const daysListedCounts = useMemo(() => bucketCounts((p) => p.days_bucket), [vehiclePoints]);
  const listingOccCounts = useMemo(() => bucketCounts((p) => p.listing_bucket), [vehiclePoints]);
  const totalBidsCounts = useMemo(() => bucketCounts((p) => p.bids_bucket), [vehiclePoints]);
  const repriceCounts = useMemo(() => bucketCounts((p) => p.reprice_bucket), [vehiclePoints]);
  const vdpCounts = useMemo(() => bucketCounts((p) => p.vdp_bucket ?? -1), [vehiclePoints]);
  const workbookCounts = useMemo(() => bucketCounts((p) => p.workbook_bucket ?? -1), [vehiclePoints]);

  const priceCounts = useMemo(() => {
    const counts: Record<PriceBucketKey, number> = { price_under: 0, price_right: 0, price_high: 0, price_over: 0 };
    for (const p of vehiclePoints) {
      const key = priceKeyFromIndex(p.price_bucket);
      if (key) counts[key] += p.count;
    }
    return counts;
  }, [vehiclePoints]);

  const togglePriority = (k: PriorityKey) =>
    setSelectedPriorities((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const toggleIndex = (setter: React.Dispatch<React.SetStateAction<number[]>>) => (i: number) =>
    setter((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
  const toggleDaysListed = toggleIndex(setSelectedDaysListed);
  const toggleListingOcc = toggleIndex(setSelectedListingOcc);
  const toggleTotalBids = toggleIndex(setSelectedTotalBids);
  const toggleReprice = toggleIndex(setSelectedReprice);
  const toggleVdp = toggleIndex(setSelectedVdp);
  const toggleWorkbook = toggleIndex(setSelectedWorkbook);
  const togglePrice = (k: PriceBucketKey) =>
    setSelectedPrice((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const anyFilterActive =
    selectedPriorities.length > 0 || selectedDaysListed.length > 0 ||
    selectedListingOcc.length > 0 || selectedTotalBids.length > 0 || selectedReprice.length > 0 ||
    selectedVdp.length > 0 || selectedWorkbook.length > 0 ||
    selectedPrice.length > 0 ||
    selectedMarketCenters.length > 0 || selectedSellers.length > 0 ||
    selectedAuctions.length > 0 || vinFilter.trim().length > 0 ||
    selectedMatchTypes.length > 0 || selectedConditions.length > 0 || selectedEngaged.length > 0 ||
    selectedSupply.length > 0 || selectedDemand.length > 0 || selectedInspection.length > 0 ||
    selectedStatus.length > 0;

  const handleResetFilters = () => {
    setActiveView(null);
    writeActiveViewRef(null);
    setSelectedPriorities([]);
    setSelectedDaysListed([]);
    setSelectedListingOcc([]);
    setSelectedTotalBids([]);
    setSelectedReprice([]);
    setSelectedVdp([]);
    setSelectedWorkbook([]);
    setSelectedPrice([]);
    setSelectedMarketCenters([]);
    setSelectedSellers([]);
    setSelectedAuctions([]);
    setVinFilter('');
    setSelectedMatchTypes([]);
    setSelectedConditions([]);
    setConditionMode('exclude');
    setSelectedEngaged([]);
    setSelectedSupply([]);
    setSelectedDemand([]);
    setSelectedInspection([]);
    setSelectedStatus([]);
  };

  // §14.10 saved filter sets: snapshot the whole broker selection so it can be
  // saved under a name + re-applied in one click (brokers have no territory
  // auto-scoping, so they re-pick the same auctions/filters every session).
  const currentFilterSelection = useMemo<BrokerFilterSelection>(() => ({
    marketCenters: selectedMarketCenters,
    auctions: selectedAuctions,
    sellers: selectedSellers,
    vin: vinFilter,
    matchTypes: selectedMatchTypes,
    priorities: selectedPriorities,
    daysListed: selectedDaysListed,
    listingOcc: selectedListingOcc,
    totalBids: selectedTotalBids,
    reprice: selectedReprice,
    vdp: selectedVdp,
    workbook: selectedWorkbook,
    price: selectedPrice,
    conditions: selectedConditions,
    conditionMode,
    engaged: selectedEngaged,
    supply: selectedSupply,
    demand: selectedDemand,
    inspection: selectedInspection,
    status: selectedStatus,
  }), [selectedMarketCenters, selectedAuctions, selectedSellers, vinFilter, selectedMatchTypes,
    selectedPriorities, selectedDaysListed, selectedListingOcc, selectedTotalBids, selectedReprice,
    selectedVdp, selectedWorkbook, selectedPrice, selectedConditions, conditionMode, selectedEngaged,
    selectedSupply, selectedDemand, selectedInspection, selectedStatus]);

  // The UNFILTERED lists on purpose: filteredOptions is narrowed by the current
  // selection, which would flag perfectly valid saved values as stale.
  const savedViewAvailable = useMemo<BrokerViewAvailable>(() => ({
    marketCenters: allMarketCenters,
    auctions: allAuctions,
    sellers: allSellers,
  }), [allMarketCenters, allAuctions, allSellers]);

  const applyFilterSet = useCallback((cfg: BrokerFilterSet) => {
    setSelectedMarketCenters(cfg.marketCenters ?? []);
    setSelectedAuctions(cfg.auctions ?? []);
    setSelectedSellers(cfg.sellers ?? []);
    setVinFilter(cfg.vin ?? '');
    setSelectedMatchTypes((cfg.matchTypes ?? []) as MatchType[]);
    setSelectedPriorities((cfg.priorities ?? []) as PriorityKey[]);
    setSelectedDaysListed(cfg.daysListed ?? []);
    setSelectedListingOcc(cfg.listingOcc ?? []);
    setSelectedTotalBids(cfg.totalBids ?? []);
    setSelectedReprice(cfg.reprice ?? []);
    setSelectedVdp(cfg.vdp ?? []);
    setSelectedWorkbook(cfg.workbook ?? []);
    setSelectedPrice((cfg.price ?? []) as PriceBucketKey[]);
    setSelectedConditions(cfg.conditions ?? []);
    setConditionMode(cfg.conditionMode === 'include' ? 'include' : 'exclude');
    setSelectedEngaged(cfg.engaged ?? []);
    setSelectedSupply(cfg.supply ?? []);
    setSelectedDemand(cfg.demand ?? []);
    setSelectedInspection(cfg.inspection ?? []);
    setSelectedStatus(cfg.status ?? []);
  }, []);

  const rememberActiveView = useCallback((next: { set: BrokerFilterSet; scope: SavedViewScope } | null) => {
    if (!next) {
      setActiveView(null);
      writeActiveViewRef(null);
      return;
    }
    const { name, ...selection } = next.set;
    setActiveView({ name, scope: next.scope, selection });
    writeActiveViewRef({ name, scope: next.scope });
  }, []);

  const applySavedView = useCallback((cfg: BrokerFilterSet, scope: SavedViewScope) => {
    applyFilterSet(cfg);
    rememberActiveView({ set: cfg, scope });
  }, [applyFilterSet, rememberActiveView]);

  const handleRestoreView = useCallback((cfg: BrokerFilterSet | null, scope: SavedViewScope, loaded: boolean) => {
    if (cfg) applySavedView(cfg, scope);
    // Only forget the view when the list really loaded without it (deleted or
    // renamed elsewhere); a failed fetch should still restore it next time.
    else if (loaded) writeActiveViewRef(null);
    setRestorePending(false);
  }, [applySavedView]);

  const activeViewModified = useMemo(
    () => !!activeView &&
      selectionFingerprint(activeView.selection) !== selectionFingerprint(currentFilterSelection),
    [activeView, currentFilterSelection],
  );

  // §14.17: jump from a VIN's recommended buyer to that buyer on the Buyer tab
  // (to shop other fitting inventory for them). Scopes the Buyer board to this
  // buyer + switches tabs + closes the VIN pop-out; the basket is untouched.
  const handleFocusBuyer = useCallback((uid: string, name?: string | null) => {
    if (!uid) return;
    setFocusedBuyers((prev) => (
      prev.some((b) => b.uid === uid)
        ? prev.filter((b) => b.uid !== uid)
        : [...prev, { uid, name: name ?? null }]
    ));
    setGroupBy('buyer');
    setVinPopoutOpen(false);
  }, []);

  // Jump from a VIN to its cohort cluster ("show me the other 2024 Pilots").
  // The key is resolved server-side so it always matches the carousel's grain.
  const handleOpenCluster = useCallback(async (vin: string) => {
    if (!vin) return;
    try {
      const res = await siftApiClient.getBrokerViewVinCluster(vin);
      setPendingClusterKey(res.cluster_key);
      setGroupBy('cluster');
      setVinPopoutOpen(false);
    } catch {
      // Still land the broker on the Cluster tab if the lookup fails.
      setGroupBy('cluster');
      setVinPopoutOpen(false);
    }
  }, []);

  // §14.18: jump to a seller from the board search — scope the Seller tab to them.
  // Scopes on the UID, not the name: 18 board names cover more than one seller
  // (DRIVETIME spans 12, 7,352 VINs), so a name scope silently pulled in the rest.
  const handlePickSeller = useCallback((sellerName: string, sellerUid: string) => {
    const token = sellerUid || sellerName;
    if (!token) return;
    setSelectedSellers([token]);
    setFocusedBuyers([]);
    setGroupBy('seller');
    setVinPopoutOpen(false);
  }, []);

  // §14.18: the board search is a multi-select seller filter (sellers and dealer
  // groups both resolve to seller UIDs), so it replaces the whole token list.
  const handleChangeSellers = useCallback((next: string[]) => {
    setSelectedSellers(next);
    if (next.length > 0) {
      setFocusedBuyers([]);
      setGroupBy('seller');
      setVinPopoutOpen(false);
    }
  }, []);

  // ----- VIN drilldown → inline VinDetailsPopout (replaces dealer popout slot) -----
  const openVinByVin = useCallback(async (vin: string) => {
    if (!vin) return;
    setVinPopoutOpen(true);
    setSelectedVehicle(null);
    setLoadingVehicle(true);
    try {
      const full = await siftApiClient.getVehicleByVin(vin);
      setSelectedVehicle(apiVehicleToSIFTVehicle(full));
    } catch (err) {
      console.error('Failed to load vehicle detail', err);
    } finally {
      setLoadingVehicle(false);
    }
  }, []);

  const handleVinClick = (row: ClientViewVinRow) => openVinByVin(row.vin);
  const handleCloseVinPopout = () => {
    setVinPopoutOpen(false);
    setSelectedVehicle(null);
  };

  // §14.18: opening a VIN from the board search should also bring its SELLER into
  // the carousel — switch to the Seller tab + select the seller card (the existing
  // selectedDealerId effect pages to it + scrolls it into view), then open the VIN.
  const handlePickVin = useCallback((vin: string, sellerUid?: string | null) => {
    if (!vin) return;
    if (sellerUid) {
      setFocusedBuyers([]);
      setGroupBy('seller');
      setSelectedDealerId(String(sellerUid));
    }
    void openVinByVin(vin);
  }, [openVinByVin]);

  // ----- Buyer basket -----
  // Pulse the basket toggle so a stage is visible without opening the panel.
  const flashBasket = useCallback(() => {
    if (basketFlashTimerRef.current) window.clearTimeout(basketFlashTimerRef.current);
    setBasketFlash(true);
    basketFlashTimerRef.current = window.setTimeout(() => setBasketFlash(false), 650);
  }, []);

  useEffect(() => () => {
    if (basketFlashTimerRef.current) window.clearTimeout(basketFlashTimerRef.current);
  }, []);

  // One state write for any number of buyers on one VIN, so "Add all" doesn't fire
  // N sequential updates (and N debounced persists).
  const handleAddManyBuyersToBasket = useCallback((buyers: BrokerBuyerMatch[], vin: string) => {
    if (buyers.length === 0) return;
    for (const b of buyers) track('broker_view', 'basket', 'add', { vin, buyer_uid: b.buyer_uid });
    // Label + seller from the open vehicle when it matches, else the raw VIN.
    const matchOpen = selectedVehicle && selectedVehicle.vin === vin;
    const label =
      matchOpen
        ? `${selectedVehicle.model_year ?? ''} ${selectedVehicle.make ?? ''} ${selectedVehicle.model ?? ''}`.trim() || vin
        : vin;
    const seller = matchOpen ? (selectedVehicle.seller_name || null) : null;
    const seller_id = matchOpen ? (selectedVehicle.seller_id || null) : null;
    const seller_loc = matchOpen
      ? ([selectedVehicle.location_state ?? selectedVehicle.state, selectedVehicle.seller_zip_code || selectedVehicle.zip_code]
          .filter(Boolean)
          .join(' · ') || null)
      : null;
    const item: BasketVin = { vin, label, seller, seller_id, seller_loc };
    setBasket((prev) => {
      const next = new Map(prev);
      for (const buyer of buyers) {
        const existing = next.get(buyer.buyer_uid);
        if (existing) {
          if (!existing.vins.some((v) => v.vin === vin)) {
            next.set(buyer.buyer_uid, { ...existing, vins: [...existing.vins, item] });
          }
        } else {
          next.set(buyer.buyer_uid, { buyer, vins: [item] });
        }
      }
      return next;
    });
    flashBasket();
  }, [selectedVehicle, flashBasket]);

  const handleAddBuyerToBasket = useCallback(
    (buyer: BrokerBuyerMatch, vin: string) => handleAddManyBuyersToBasket([buyer], vin),
    [handleAddManyBuyersToBasket],
  );

  // Board (Buyer-lens) buyers arrive with their whole matching VIN set — stage
  // the buyer plus all their VINs (deduped) in one action.
  const handleAddBoardBuyerToBasket = useCallback(
    (buyer: BasketBuyerInfo, vins: BasketVin[]) => {
      setBasket((prev) => {
        const next = new Map(prev);
        const existing = next.get(buyer.buyer_uid);
        if (existing) {
          const seen = new Set(existing.vins.map((v) => v.vin));
          const merged = [...existing.vins, ...vins.filter((v) => !seen.has(v.vin))];
          next.set(buyer.buyer_uid, { ...existing, vins: merged });
        } else {
          next.set(buyer.buyer_uid, { buyer, vins: [...vins] });
        }
        return next;
      });
      flashBasket();
    },
    [flashBasket],
  );

  // Stage ONE VIN under SEVERAL buyers in a single state write, so the per-row
  // "+" doesn't fire five sequential updates (and five debounced persists).
  const handleAddBuyersToBasket = useCallback(
    (buyers: BasketBuyerInfo[], vinItem: BasketVin) => {
      if (buyers.length === 0) return;
      setBasket((prev) => {
        const next = new Map(prev);
        for (const buyer of buyers) {
          const existing = next.get(buyer.buyer_uid);
          if (existing) {
            if (!existing.vins.some((x) => x.vin === vinItem.vin)) {
              next.set(buyer.buyer_uid, { ...existing, vins: [...existing.vins, vinItem] });
            }
          } else {
            next.set(buyer.buyer_uid, { buyer, vins: [vinItem] });
          }
        }
        return next;
      });
      flashBasket();
    },
    [flashBasket],
  );

  // Per-row add-to-basket on the unified table. Defaults to the VIN's best buyer;
  // the row's caret menu opts into staging its top N candidates at once, so a run
  // of vehicles can build a cross-consideration set. The Buyer tab is already
  // scoped to one contact, so it always stages just that buyer.
  const handleAddSliceVinToBasket = useCallback(async (v: BrokerSliceVinRow, topN = 1) => {
    const label = [v.model_year, v.make, v.model].filter(Boolean).join(' ') || v.vin;
    const seller_loc = [v.territory_state, v.seller_zip_code].filter(Boolean).join(' · ') || null;
    const vinItem: BasketVin = {
      vin: v.vin,
      label,
      seller: v.seller_name ?? null,
      seller_id: v.seller_id ?? null,
      seller_loc,
    };
    const fallback: BasketBuyerInfo[] = v.buyer_uid
      ? [{ buyer_uid: v.buyer_uid, buyer_name: v.buyer_name ?? null, buyer_zip_code: null, state: null }]
      : [];

    if (topN <= 1 || groupBy === 'buyer') {
      handleAddBuyersToBasket(fallback, vinItem);
      return;
    }
    try {
      const res = await siftApiClient.getBrokerViewVinBuyers(v.vin, topN);
      const buyers: BasketBuyerInfo[] = (res.buyers ?? []).map((b) => ({
        buyer_uid: b.buyer_uid,
        buyer_name: b.buyer_name ?? null,
        buyer_zip_code: b.buyer_zip_code ?? null,
        state: b.state ?? null,
      }));
      handleAddBuyersToBasket(buyers.length ? buyers : fallback, vinItem);
    } catch {
      // Fall back to the row's resolved best buyer so the click still works.
      handleAddBuyersToBasket(fallback, vinItem);
    }
  }, [groupBy, handleAddBuyersToBasket]);

  const handleRemoveBasketVin = useCallback((buyerUid: string, vin: string) => {
    setBasket((prev) => {
      const next = new Map(prev);
      const existing = next.get(buyerUid);
      if (!existing) return prev;
      const vins = existing.vins.filter((v) => v.vin !== vin);
      if (vins.length === 0) next.delete(buyerUid);
      else next.set(buyerUid, { ...existing, vins });
      return next;
    });
  }, []);

  // The row ✓ in a VIN table means "this VIN is staged under someone"; clicking it
  // again undoes the add for every buyer it was staged under.
  const handleRemoveVinFromBasket = useCallback((vin: string) => {
    setBasket((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [uid, entry] of prev) {
        const vins = entry.vins.filter((v) => v.vin !== vin);
        if (vins.length === entry.vins.length) continue;
        changed = true;
        if (vins.length === 0) next.delete(uid);
        else next.set(uid, { ...entry, vins });
      }
      return changed ? next : prev;
    });
  }, []);

  const handleRemoveBasketBuyer = useCallback((buyerUid: string) => {
    setBasket((prev) => {
      const next = new Map(prev);
      next.delete(buyerUid);
      return next;
    });
  }, []);

  const handleClearBasket = useCallback(() => setBasket(new Map()), []);

  // Stage a "next best" unit under the buyer it was suggested for.
  const handleAddBasketSuggestion = useCallback(
    (buyer: BasketBuyerInfo, s: BrokerBasketSuggestion) => {
      handleAddBuyersToBasket([buyer], {
        vin: s.vin,
        label: [s.model_year, s.make, s.model].filter(Boolean).join(' ') || s.vin,
        seller: s.seller_name ?? null,
        seller_id: s.seller_id ?? null,
        seller_loc: [s.seller_state, s.seller_zip].filter(Boolean).join(' · ') || null,
      });
    },
    [handleAddBuyersToBasket],
  );

  // §2.6 copy an already-staged VIN to another buyer. Reuses the add path, which
  // already dedupes within a buyer, so re-picking the same buyer is a no-op.
  const handleAssignVinToBuyer = useCallback(
    (buyer: BasketBuyerInfo, vinItem: BasketVin) => {
      handleAddBuyersToBasket([buyer], vinItem);
    },
    [handleAddBuyersToBasket],
  );

  // Basket → jump back to the underlying entity so a broker can re-open its
  // details. Close the basket first so the result is visible behind it.
  const handleBasketOpenVin = useCallback((vin: string, sellerUid?: string | null) => {
    setBasketOpen(false);
    handlePickVin(vin, sellerUid);
  }, [handlePickVin]);

  const handleBasketOpenSeller = useCallback((sellerName?: string | null, sellerUid?: string | null) => {
    setBasketOpen(false);
    if (sellerUid) {
      setFocusedBuyers([]);
      setGroupBy('seller');
      setSelectedDealerId(String(sellerUid));
    } else if (sellerName) {
      handlePickSeller(sellerName, '');
    }
  }, [handlePickSeller]);

  const handleBasketOpenBuyer = useCallback((uid: string, name?: string | null) => {
    setBasketOpen(false);
    handleFocusBuyer(uid, name);
  }, [handleFocusBuyer]);

  // §10-C hydrate the persisted basket once on mount. `lastPersistedBasketRef`
  // is seeded with the loaded blob so the hydration render itself is a no-op for
  // the persist effect below (no write-back on load).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const arr = await siftApiClient.getBrokerBasket<BasketBuyer>();
        if (cancelled) return;
        lastPersistedBasketRef.current = JSON.stringify(arr);
        if (arr.length) setBasket(new Map(arr.map((b) => [b.buyer.buyer_uid, b])));
      } catch {
        // Keep an empty basket if the config can't be read — non-fatal.
      } finally {
        if (!cancelled) basketHydratedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // §10-C persist the whole basket (debounced) whenever it changes after
  // hydration. The serialization guard skips no-op renders (incl. the hydration
  // load) so we only hit Snowflake on real changes.
  useEffect(() => {
    if (!basketHydratedRef.current) return;
    const payload = Array.from(basket.values());
    const serialized = JSON.stringify(payload);
    if (serialized === lastPersistedBasketRef.current) return;
    const t = window.setTimeout(() => {
      void siftApiClient
        .saveBrokerBasket(payload)
        .then(() => {
          lastPersistedBasketRef.current = serialized;
        })
        .catch(() => {
          // Leave the ref unchanged so the next change retries the write.
        });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [basket]);

  const basketBuyers = useMemo(() => Array.from(basket.values()), [basket]);
  const basketVinCount = useMemo(
    () => basketBuyers.reduce((n, b) => n + b.vins.length, 0),
    [basketBuyers],
  );
  // VINs staged under ANY buyer — the unified table's per-row "+" marks a VIN as
  // added once it's in the basket, since one click can stage several buyers.
  const basketVins = useMemo(() => {
    const vins = new Set<string>();
    for (const b of basketBuyers) {
      for (const v of b.vins) vins.add(v.vin);
    }
    return vins;
  }, [basketBuyers]);
  // buyer_uids that already have the currently-open VIN staged (for "Added" state).
  const basketKeysForCurrentVin = useMemo(() => {
    const vin = selectedVehicle?.vin;
    if (!vin) return new Set<string>();
    const keys = new Set<string>();
    for (const b of basketBuyers) {
      if (b.vins.some((v) => v.vin === vin)) keys.add(b.buyer.buyer_uid);
    }
    return keys;
  }, [basketBuyers, selectedVehicle]);

  // Territory filters describing the current board — shared by the Buyer-lens
  // aggregation so it scopes to the same INCLUDE-VIN set as the carousel/map.
  const boardParams = useMemo(() => ({
    market_center: selectedMarketCenters.length > 0 ? selectedMarketCenters.join(',') : undefined,
    seller_name: selectedSellers.length > 0 ? selectedSellers.join(',') : undefined,
    auction: selectedAuctions.length > 0 ? selectedAuctions.join(',') : undefined,
    vin: vinFilter.trim() ? vinFilter.trim() : undefined,
    impersonate_role: impersonatedRole || undefined,
  }), [selectedMarketCenters, selectedSellers, selectedAuctions, vinFilter, impersonatedRole]);

  // The board-scope filters the shared VIN table reads (market center / seller /
  // auction). The board's VIN search is intentionally excluded — the unified
  // table owns its own per-slice VIN search, and the card list is already scoped.
  const sliceBoardParams = useMemo(() => ({
    market_center: boardParams.market_center,
    seller_name: boardParams.seller_name,
    auction: boardParams.auction,
  }), [boardParams.market_center, boardParams.seller_name, boardParams.auction]);

  const boardParamsKey = useMemo(() => JSON.stringify(boardParams), [boardParams]);

  // Chip selections serialized as CSV bucket-index params — passed to the buyer /
  // cluster card lists AND the shared VIN table so the urgency + vehicle filters
  // apply server-side, identically, on every tab.
  const chipFilterParams = useMemo(() => {
    const csv = (arr: number[]) => (arr.length ? arr.join(',') : undefined);
    return {
      priorities: csv(selectedPriorities.map((k) => PRIORITY_INDEX[k])),
      days_listed: csv(selectedDaysListed),
      listing_occ: csv(selectedListingOcc),
      total_bids: csv(selectedTotalBids),
      reprice: csv(selectedReprice),
      vdp_views: csv(selectedVdp),
      workbook_saves: csv(selectedWorkbook),
      price: csv(selectedPrice.map((k) => PRICE_INDEX[k])),
      conditions: selectedConditions.length ? selectedConditions.join(',') : undefined,
      conditions_mode: selectedConditions.length ? conditionMode : undefined,
      engaged: selectedEngaged.length ? selectedEngaged.join(',') : undefined,
      supply: selectedSupply.length ? selectedSupply.join(',') : undefined,
      demand: selectedDemand.length ? selectedDemand.join(',') : undefined,
      inspection: selectedInspection.length ? selectedInspection.join(',') : undefined,
      broker_status: selectedStatus.length ? selectedStatus.join(',') : undefined,
    };
  }, [selectedPriorities, selectedDaysListed, selectedListingOcc, selectedTotalBids, selectedReprice, selectedPrice, selectedConditions, conditionMode, selectedEngaged, selectedSupply, selectedDemand, selectedVdp, selectedWorkbook, selectedInspection, selectedStatus]);
  // The epoch is part of the key so a settings save invalidates the slice caches
  // the same way a filter change does.
  const chipFilterKey = useMemo(
    () => JSON.stringify(chipFilterParams) + `|s${settingsEpoch}`,
    [chipFilterParams, settingsEpoch],
  );
  // Buyer-Match filter as a CSV — also drives the buyer/cluster CARD lists (not
  // just the VIN table), so card counts + ranking reflect it.
  const matchTypesCsv = useMemo(
    () => (selectedMatchTypes.length ? selectedMatchTypes.join(',') : undefined),
    [selectedMatchTypes],
  );

  // Cluster bulk-add (§5 regression): one click stages ALL of a cluster's VINs to
  // its single top buyer. The card only samples the most-severe VINs, so fetch the
  // full board-scoped set, then merge them under the top buyer (dedup handled by
  // handleAddBoardBuyerToBasket). No-op for a cluster with no shared top buyer.
  const handleBulkAddCluster = useCallback(async (cluster: BrokerBoardCluster) => {
    const key = cluster.cluster_key;
    if (!key || !cluster.top_buyer_uid) return;
    setBulkAddingCluster(key);
    try {
      const vins = await fetchClusterBasketVins(key, cluster, {
        ...sliceBoardParams, ...chipFilterParams, match_types: matchTypesCsv,
      });
      if (vins.length === 0) return;
      handleAddBoardBuyerToBasket(
        { buyer_uid: cluster.top_buyer_uid, buyer_name: cluster.top_buyer_name ?? null, buyer_zip_code: null, state: null },
        vins,
      );
    } catch {
      // Leave the basket unchanged; the broker can retry.
    } finally {
      setBulkAddingCluster(null);
    }
  }, [sliceBoardParams, chipFilterParams, matchTypesCsv, handleAddBoardBuyerToBasket]);

  // The right pane shows the VIN pop-out whenever one is open (regardless of the
  // map toggle), else the map only when toggled on; otherwise it's hidden and the
  // carousel/table pane spans the full width.
  const rightPaneVisible = vinPopoutOpen || showMap;

  return (
    <div className="bv-dashboard">
      {/* Entry filters */}
      <div className="bv-filters-row">
        <div className="bv-filter-group">
          <MultiSelectDropdown label="Market Center" options={filteredOptions.marketCenters} values={selectedMarketCenters} onChange={setSelectedMarketCenters} placeholder="All Market Centers" />
          <MultiSelectDropdown label="Auction" options={allAuctions} values={selectedAuctions} onChange={setSelectedAuctions} placeholder="All Auctions" />
          <BrokerBoardSearch
            selectedSellers={selectedSellers}
            onChangeSellers={handleChangeSellers}
            onPickBuyer={handleFocusBuyer}
            onPickVin={handlePickVin}
            onFilterVehicles={setVinFilter}
            vehicleFilter={vinFilter}
            focusedBuyers={focusedBuyers}
            onToggleBuyer={handleFocusBuyer}
            onClearBuyers={() => setFocusedBuyers([])}
            marketCenters={selectedMarketCenters}
            auctions={selectedAuctions}
          />
        </div>
        <div className="bv-data-freshness">
          <span
            className="bv-data-freshness-line"
            title={`VIN data last updated ${formatIngestTs(vinDataUpdated)}`}
          >
            VIN <strong>{formatIngestAge(vinDataUpdated)}</strong>
          </span>
          <span
            className="bv-data-freshness-line"
            title={`Buyer data last updated ${formatIngestTs(buyerDataUpdated)}`}
          >
            Buyer <strong>{formatIngestAge(buyerDataUpdated)}</strong>
          </span>
        </div>
      </div>

      {/* Combined chips row — same urgency + vehicle filters on every tab, so the
          Seller / Buyer / Cluster views stay near-identical (just different slices). */}
      <div className="bv-control-row">
        <div className="bv-chip-rows">
          <PriorityChipRow counts={priorityCounts} selected={selectedPriorities} onToggle={togglePriority} />
          <FiltersChipRow
            daysCounts={daysListedCounts}
            daysSelected={selectedDaysListed}
            onToggleDays={toggleDaysListed}
            listingCounts={listingOccCounts}
            listingSelected={selectedListingOcc}
            onToggleListing={toggleListingOcc}
            bidsCounts={totalBidsCounts}
            bidsSelected={selectedTotalBids}
            onToggleBids={toggleTotalBids}
            repriceCounts={repriceCounts}
            repriceSelected={selectedReprice}
            onToggleReprice={toggleReprice}
            vdpCounts={vdpCounts}
            vdpSelected={selectedVdp}
            onToggleVdp={toggleVdp}
            workbookCounts={workbookCounts}
            workbookSelected={selectedWorkbook}
            onToggleWorkbook={toggleWorkbook}
            priceCounts={priceCounts}
            priceSelected={selectedPrice}
            onTogglePrice={togglePrice}
            matchOptions={MATCH_TYPE_OPTIONS}
            matchSelected={selectedMatchTypes}
            onToggleMatch={(v) =>
              setSelectedMatchTypes((s) => (s.includes(v as MatchType) ? s.filter((x) => x !== v) : [...s, v as MatchType]))
            }
            conditionSelected={selectedConditions}
            onToggleCondition={(v) =>
              setSelectedConditions((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))
            }
            conditionMode={conditionMode}
            onSetConditionMode={setConditionMode}
            engagedSelected={selectedEngaged}
            onToggleEngaged={(v) =>
              setSelectedEngaged((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))
            }
            supplySelected={selectedSupply}
            onToggleSupply={(v) =>
              setSelectedSupply((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))
            }
            inspectionSelected={selectedInspection}
            onToggleInspection={(v) =>
              setSelectedInspection((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))
            }
            demandSelected={selectedDemand}
            onToggleDemand={(v) =>
              setSelectedDemand((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))
            }
            statusSelected={selectedStatus}
            onToggleStatus={(v) =>
              setSelectedStatus((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]))
            }
          />
        </div>
        <div className="bv-inspector-group">
          <div className="bv-inspector-line bv-inspector-meta">
            <span className="bv-stat">
              {(loading || !defaultsApplied) ? '...' : sortedDealers.length} of {totalDealerCount} dealer{totalDealerCount !== 1 ? 's' : ''}
            </span>
            {anyFilterActive && (
              <button className="bv-reset-btn" onClick={handleResetFilters}>Reset</button>
            )}
            <SavedFilterSets
              current={currentFilterSelection}
              onApply={applySavedView}
              available={savedViewAvailable}
              activeView={activeView}
              activeModified={activeViewModified}
              restoreView={restoreViewRef}
              onRestore={handleRestoreView}
              onActiveViewChange={rememberActiveView}
            />
            <button
              className={`bv-map-toggle${showMap ? ' bv-map-toggle--on' : ''}`}
              onClick={() => setShowMap((m) => !m)}
              title={showMap ? 'Hide the map (show the table pane full width)' : 'Show the map alongside the table'}
              aria-pressed={showMap}
            >
              🗺️ Map {showMap ? 'On' : 'Off'}
            </button>
            <button
              className={`bv-basket-toggle${basketFlash ? ' bv-basket-toggle--flash' : ''}`}
              onClick={() => setBasketOpen((o) => !o)}
              title="Buyer outreach basket"
            >
              🧺 Basket{basket.size > 0 ? ` (${basket.size}/${basketVinCount})` : ''}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="bv-error">{error}</div>}

      {/* Content: carousel + (map or popout) with drag-resize splitter */}
      <div className="bv-content">
        <div className="bv-carousel" style={rightPaneVisible ? { width: carouselWidth } : { flex: 1, minWidth: 0 }}>
          <div className="bv-carousel-header">
            <div className="bv-groupby" role="tablist" aria-label="Group by">
              {/* Buyer lens re-enabled (§10-A): it now derives from the same
                  precomputed per-VIN top-5 buyer matches as the popout — a buyer
                  appears against a VIN only if it's in that VIN's top-5. */}
              {(['seller', 'buyer', 'cluster'] as const).map((g) => (
                <button
                  key={g}
                  role="tab"
                  aria-selected={groupBy === g}
                  className={`bv-groupby-tab${groupBy === g ? ' bv-groupby-tab--active' : ''}`}
                  onClick={() => { setGroupBy(g); setFocusedBuyers([]); }}
                  title={GROUP_BY_HELP[g]}
                >
                  {g === 'seller' ? 'Seller' : g === 'buyer' ? 'Buyer' : 'Cluster'}
                </button>
              ))}
              <span
                className="bv-groupby-help"
                role="img"
                aria-label="What do these views do?"
                title={`How to work this board:\n\n${GROUP_BY_HELP.seller}\n\n${GROUP_BY_HELP.buyer}\n\n${GROUP_BY_HELP.cluster}`}
              >ⓘ</span>
            </div>
            {groupBy === 'seller' && (
              <span className="bv-carousel-count">
                {sortedDealers.length === 0
                  ? '0 dealers'
                  : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, sortedDealers.length)} of ${sortedDealers.length.toLocaleString()}`}
              </span>
            )}
          </div>
          {groupBy === 'buyer' && focusedBuyers.length > 0 && (
            <div className="bv-buyer-focus-banner">
              <span>
                Shopping for{' '}
                <strong>
                  {focusedBuyers.length === 1
                    ? (focusedBuyers[0].name || `Buyer ${focusedBuyers[0].uid}`)
                    : `${focusedBuyers.length} buyers`}
                </strong>{' '}
                {focusedBuyers.length === 1 && (
                  <span className="bv-buyer-focus-id">#{focusedBuyers[0].uid}</span>
                )}
                {' '}— inventory {focusedBuyers.length === 1 ? 'this buyer fits' : 'these buyers fit'}
              </span>
              <button
                type="button"
                className="bv-buyer-focus-clear"
                onClick={() => setFocusedBuyers([])}
                title="Show all board buyers again"
              >✕ Clear</button>
            </div>
          )}
          {groupBy === 'buyer' && focusedBuyers.length === 0 && (
            <div className="bv-buyer-lost-controls">
              <button
                type="button"
                className={`bv-buyer-lost-btn${buyerLostFirst ? ' bv-buyer-lost-btn-on' : ''}`}
                onClick={() => setBuyerLostFirst((v) => !v)}
                title="Sort buyers with the most recent lost bids (bid but didn't win) to the top. Pair them with the similar available units on their card."
              >⟳ Lost bidders first</button>
              <label className="bv-buyer-lost-check" title="Show only buyers with a recent lost bid — a focused re-engagement list.">
                <input
                  type="checkbox"
                  checked={buyerLostOnly}
                  onChange={(e) => setBuyerLostOnly(e.target.checked)}
                />
                Only lost bidders
              </label>
            </div>
          )}
          <div className="bv-carousel-list" ref={carouselListRef}>
            {groupBy === 'buyer' ? (
              <BrokerSliceView<BrokerBoardBuyer>
                key="buyer"
                slice="buyer"
                boardParams={sliceBoardParams}
                matchTypes={selectedMatchTypes}
                chipFilters={chipFilterParams}
                fetchKey={`buyer:${boardParamsKey}:${chipFilterKey}:${matchTypesCsv ?? ''}:${focusedBuyers.map((b) => b.uid).join(',')}:${buyerLostFirst ? 'lf' : ''}:${buyerLostOnly ? 'lo' : ''}`}
                fetchCards={async ({ limit, offset, signal }) => {
                  const res = await siftApiClient.getBrokerViewBoardBuyers({ ...boardParams, ...chipFilterParams, match_types: matchTypesCsv, buyer: focusedBuyers.length > 0 ? focusedBuyers.map((b) => b.uid).join(',') : undefined, sort: buyerLostFirst ? 'lost' : 'reach', lost_only: buyerLostOnly || undefined, limit, offset }, signal);
                  return { cards: res.buyers, total: res.total_buyers, matchesWarming: res.matches_warming };
                }}
                getSlotKey={(b) => b.buyer_uid}
                getCardId={(b, i) => b.buyer_uid || String(i)}
                renderCardHeader={(b, rank) => <BrokerBuyerCardHeader buyer={b} rank={rank} />}
                renderCardActions={(b) => <BrokerBuyerCopyId buyer={b} />}
                renderListHeader={(from, to, total) => `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()} buyers · ranked by urgency, then match tightness`}
                labels={{ loading: 'Ranking buyers across the board…', empty: 'No buyers match the current board.', error: 'Could not load buyers for this board.' }}
                showBuyer={false}
                onVinOpen={openVinByVin}
                onAddToBasket={handleAddSliceVinToBasket}
                basketVins={basketVins}
                onRemoveFromBasket={handleRemoveVinFromBasket}
                onExpandChange={(card) => setMapFocus(card ? { kind: 'buyer', key: card.buyer_uid, name: card.buyer_name || `Buyer ${card.buyer_uid}` } : null)}
              />
            ) : groupBy === 'cluster' ? (
              <BrokerSliceView<BrokerBoardCluster>
                key="cluster"
                slice="cluster"
                boardParams={sliceBoardParams}
                matchTypes={selectedMatchTypes}
                chipFilters={chipFilterParams}
                fetchKey={`cluster:${boardParamsKey}:${chipFilterKey}:${matchTypesCsv ?? ''}`}
                fetchCards={async ({ limit, offset, signal }) => {
                  const res = await siftApiClient.getBrokerViewBoardClusters({ ...boardParams, ...chipFilterParams, match_types: matchTypesCsv, limit, offset, focus_key: pendingClusterKey ?? undefined }, signal);
                  return { cards: res.clusters, total: res.total_clusters, matchesWarming: res.matches_warming, focusRank: res.focus_rank };
                }}
                getSlotKey={(c) => c.cluster_key ?? ''}
                getCardId={(c, i) => c.cluster_key ?? `${c.make}-${c.model}-${i}`}
                renderCardHeader={(c) => (
                  <BrokerClusterCardHeader
                    cluster={c}
                    onBulkAdd={handleBulkAddCluster}
                    bulkAdding={bulkAddingCluster === (c.cluster_key ?? '')}
                  />
                )}
                renderListHeader={(from, to, total) => `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()} cohort clusters · ranked by urgency`}
                labels={{ loading: 'Grouping similar vehicles…', empty: 'No cohort clusters on the current board.', error: 'Could not load clusters for this board.' }}
                showBuyer
                onVinOpen={openVinByVin}
                onAddToBasket={handleAddSliceVinToBasket}
                basketVins={basketVins}
                onRemoveFromBasket={handleRemoveVinFromBasket}
                autoExpandId={pendingClusterKey}
                onExpandChange={(card) => {
                  // One-shot: consumed on arrival so later filter changes don't re-open it.
                  setPendingClusterKey(null);
                  setMapFocus(card?.cluster_key ? { kind: 'cluster', key: card.cluster_key, name: [card.make, card.model].filter(Boolean).join(' ') || 'Cluster' } : null);
                }}
              />
            ) : (loading || !defaultsApplied) && pagedAggregates.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bv-card-skeleton" aria-hidden="true">
                  <div className="bv-skeleton bv-skeleton-line" />
                  <div className="bv-skeleton bv-skeleton-line-sm" />
                  <div className="bv-skeleton bv-skeleton-block" />
                  <div className="bv-skeleton-pills">
                    <div className="bv-skeleton bv-skeleton-pill" />
                    <div className="bv-skeleton bv-skeleton-pill" />
                    <div className="bv-skeleton bv-skeleton-pill" />
                  </div>
                </div>
              ))
            ) : pagedAggregates.length === 0 ? (
              <div className="bv-empty">
                No dealers match the current filters.
              </div>
            ) : (
              pagedAggregates.map((dealer) => (
                <BrokerViewDealerCard
                  key={dealer.seller_id}
                  dealer={dealer}
                  meta={sellerMeta.get(dealer.seller_id) ?? null}
                  selected={dealer.seller_id === selectedDealerId}
                  expanded={dealer.seller_id === expandedDealerId}
                  onClick={() => {
                    setSelectedDealerId(dealer.seller_id);
                    setExpandedDealerId((cur) => {
                      const next = cur === dealer.seller_id ? null : dealer.seller_id;
                      // §10-E: expanding a card focuses the map on it; collapsing clears.
                      setMapFocus(next ? { kind: 'seller', key: dealer.seller_id, name: dealer.seller_name || dealer.seller_id } : null);
                      return next;
                    });
                  }}
                  onVinClick={handleVinClick}
                  sliceBoardParams={sliceBoardParams}
                  matchTypes={selectedMatchTypes}
                  chipFilters={chipFilterParams}
                  onAddToBasket={handleAddSliceVinToBasket}
                  basketVins={basketVins}
                  onRemoveFromBasket={handleRemoveVinFromBasket}
                />
              ))
            )}
          </div>
          {groupBy === 'seller' && sortedDealers.length > 0 && (
            <div className="bv-pagination">
              <button
                className="bv-page-btn"
                onClick={() => setPage(1)}
                disabled={page === 1}
                aria-label="First page"
              >«</button>              <button
                className="bv-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Previous page"
              >‹</button>
              <span className="bv-page-indicator">{page} / {totalPages}</span>
              <button
                className="bv-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                aria-label="Next page"
              >›</button>
              <button
                className="bv-page-btn"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                aria-label="Last page"
              >»</button>
              <select
                className="bv-page-size"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Cards per page"
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}/page</option>)}
              </select>
            </div>
          )}
        </div>

        {rightPaneVisible && (
          <div
            ref={splitterRef}
            className="bv-splitter"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize carousel"
            onMouseDown={onSplitterMouseDown}
          />
        )}

        {vinPopoutOpen ? (
          <VinDetailsPopout
            vehicle={selectedVehicle}
            isLoading={loadingVehicle}
            onClose={handleCloseVinPopout}
            onBack={expandedDealerId ? () => setVinPopoutOpen(false) : undefined}
            backLabel="Back to Dealer"
            onAddBuyerToBasket={handleAddBuyerToBasket}
            onAddBuyersToBasket={handleAddManyBuyersToBasket}
            buyerBasketKeys={basketKeysForCurrentVin}
            onRemoveBuyerFromBasket={handleRemoveBasketVin}
            onFocusBuyer={handleFocusBuyer}
            onOpenCluster={handleOpenCluster}
          />
        ) : showMap ? (
          <div className="bv-map-container">
            <BrokerViewMap
              points={filteredVehiclePoints}
              loading={loading}
              selectedDealerId={selectedDealerId}
              onDealerClick={(id) => setSelectedDealerId(id)}
              focus={mapFocus}
              sellerMapParams={{ ...chipFilterParams, match_types: matchTypesCsv }}
              onFocusVinClick={openVinByVin}
              onClearFocus={() => setMapFocus(null)}
            />
          </div>
        ) : null}
      </div>

      <BuyerBasket
        open={basketOpen}
        buyers={basketBuyers}
        onClose={() => setBasketOpen(false)}
        onRemoveVin={handleRemoveBasketVin}
        onRemoveBuyer={handleRemoveBasketBuyer}
        onClear={handleClearBasket}
        onOpenVin={handleBasketOpenVin}
        onOpenSeller={handleBasketOpenSeller}
        onOpenBuyer={handleBasketOpenBuyer}
        onAddSuggestion={handleAddBasketSuggestion}
        onAssignVinToBuyer={handleAssignVinToBuyer}
      />
    </div>
  );
};
